import type { Dictionary } from '../../i18n/dictionary';
import {
  generateChangingCodes,
  generateChangingEmployeeNumbers,
  generateMergingQualifications,
  generateRemoveNonDefaultGroupingTypeLanguages,
  generateSplittingSkills,
} from './generators';

interface ScriptField<F extends string = string> {
  /** Must match the generator input key exactly — a typo silently falls back to the default. */
  name: F;
  id: string;
}

/** What a script page says, in one language. */
interface ScriptText<F extends string = string> {
  title: string;
  /** Shorter label when the title is too long for the sidebar. */
  navLabel?: string;
  /** Markdown — `**DBWorkspace**`, `` `en_us` `` — rendered with `<Markdown>`. */
  description: string;
  /**
   * One entry per field. The placeholder is the generator's fallback for an
   * empty field, so it reads the same in every language.
   */
  fields: Record<F, { label: string; placeholder?: string }>;
}

export interface ScriptDefinition<F extends string = string> {
  /** URL segment, nav key, and the identity of the script everywhere. */
  slug: string;
  fields: ScriptField<F>[];
  text: Dictionary<ScriptText<NoInfer<F>>>;
  generate: (values: Record<string, string>) => string;
}

/**
 * Infers the field names from `fields`, so `text.pl` and `text.en` must label
 * exactly those — a field missing a label in either language is a type error.
 */
function defineScript<const F extends string>(
  script: ScriptDefinition<F>,
): ScriptDefinition {
  return script;
}

// Field labels several scripts share.
const PL = {
  filename: 'Nazwa pliku Excel',
  sheet: 'Nazwa arkusza',
  workflow: 'UUID workflow',
  trigger: 'UUID triggera',
};
const EN: typeof PL = {
  filename: 'Excel filename',
  sheet: 'Sheet name',
  workflow: 'Workflow UUID',
  trigger: 'Trigger UUID',
};

/**
 * The single registry of AG5 script pages. Routes (`App.tsx`) and the sidebar
 * (`Ag5Layout.tsx`) are both derived from this, so adding a script means one
 * entry here plus its generator — not four files that must agree with each
 * other. See the `ag5-create-script-generator` skill.
 */
export const AG5_SCRIPTS: ScriptDefinition[] = [
  defineScript({
    slug: 'changing-codes',
    fields: [
      { name: 'filename', id: 'cc-filename' },
      { name: 'sheetName', id: 'cc-sheet' },
    ],
    text: {
      pl: {
        title: 'Zmiana kodów',
        description:
          'Zmienia kody kwalifikacji hurtowo na podstawie pliku Excel (kolumna 2 to obecny kod, kolumna 3 — docelowy; wiersz 1 jest pomijany jako nagłówek). Podaj nazwę skoroszytu i arkusza, a następnie wklej wygenerowany skrypt do **DBWorkspace** ustawionego na repozytorium klienta.',
        fields: {
          filename: { label: PL.filename, placeholder: 'ExcelFileName.xlsx' },
          sheetName: { label: PL.sheet, placeholder: 'Sheet 1' },
        },
      },
      en: {
        title: 'Changing codes',
        description:
          "Renames a batch of qualification codes from an Excel file (column 2 as current code, column 3 as target code; row 1 skipped as header). Fill in the workbook filename and sheet, then paste the generated script into a **DBWorkspace** scoped to the customer's repository.",
        fields: {
          filename: { label: EN.filename, placeholder: 'ExcelFileName.xlsx' },
          sheetName: { label: EN.sheet, placeholder: 'Sheet 1' },
        },
      },
    },
    generate: (v) => generateChangingCodes(v),
  }),
  defineScript({
    slug: 'splitting-skills',
    fields: [
      { name: 'filename', id: 'ss-filename' },
      { name: 'sheet1Name', id: 'ss-sheet1' },
      { name: 'workflow1Uuid', id: 'ss-workflow1' },
      { name: 'trigger1Uuid', id: 'ss-trigger1' },
      { name: 'sheet2Name', id: 'ss-sheet2' },
      { name: 'workflow2Uuid', id: 'ss-workflow2' },
      { name: 'trigger2Uuid', id: 'ss-trigger2' },
    ],
    text: {
      pl: {
        title: 'Podział umiejętności na dwa workflow',
        navLabel: 'Podział umiejętności',
        description:
          'Dzieli jedną listę umiejętności między dwa triggery workflow. Skoroszyt Excel musi mieć po jednym arkuszu na grupę, a każdy z nich zawiera kody kwalifikacji w kolumnie 1 (wiersz 1 jest pomijany jako nagłówek). Przed uruchomieniem odszukaj UUID workflow i triggerów w repozytorium klienta.',
        fields: {
          filename: { label: PL.filename, placeholder: 'update-prod.xlsx' },
          sheet1Name: { label: PL.sheet, placeholder: 'Sheet 1' },
          workflow1Uuid: { label: PL.workflow, placeholder: 'xxx' },
          trigger1Uuid: { label: PL.trigger, placeholder: 'xxx' },
          sheet2Name: { label: PL.sheet, placeholder: 'Sheet 2' },
          workflow2Uuid: { label: PL.workflow, placeholder: 'xxx' },
          trigger2Uuid: { label: PL.trigger, placeholder: 'xxx' },
        },
      },
      en: {
        title: 'Splitting skills into two workflows',
        navLabel: 'Splitting skills',
        description:
          "Splits a single skill list between two workflow triggers. The Excel workbook must have one sheet per bucket, each listing qualification codes in column 1 (row 1 skipped as header). Look up the workflow and trigger UUIDs in the customer's repository before running.",
        fields: {
          filename: { label: EN.filename, placeholder: 'update-prod.xlsx' },
          sheet1Name: { label: EN.sheet, placeholder: 'Sheet 1' },
          workflow1Uuid: { label: EN.workflow, placeholder: 'xxx' },
          trigger1Uuid: { label: EN.trigger, placeholder: 'xxx' },
          sheet2Name: { label: EN.sheet, placeholder: 'Sheet 2' },
          workflow2Uuid: { label: EN.workflow, placeholder: 'xxx' },
          trigger2Uuid: { label: EN.trigger, placeholder: 'xxx' },
        },
      },
    },
    generate: (v) => generateSplittingSkills(v),
  }),
  defineScript({
    slug: 'merging-qualifications',
    fields: [
      { name: 'filename', id: 'mq-filename' },
      { name: 'sheetName', id: 'mq-sheet' },
    ],
    text: {
      pl: {
        title: 'Scalanie kwalifikacji',
        description:
          'Scala zduplikowane kwalifikacje o tym samym kodzie. Plik Excel zawiera kody do scalenia w kolumnie 2 (wiersz 1 jest pomijany jako nagłówek; powtórzenia są usuwane automatycznie). Wygenerowany skrypt dzieli dopasowania na grupy `size1`, `size2` i `sizeX`, żeby można je było przejrzeć przed uruchomieniem pętli scalających.',
        fields: {
          filename: { label: PL.filename, placeholder: 'ExcelFileName.xlsx' },
          sheetName: { label: PL.sheet, placeholder: 'Sheet 1' },
        },
      },
      en: {
        title: 'Merging qualifications',
        description:
          'Collapses duplicate qualifications sharing the same code. The Excel file lists affected codes in column 2 (row 1 skipped as header; duplicates deduped automatically). The generated script partitions matches into `size1`, `size2` and `sizeX` buckets so you can inspect before running the merge loops.',
        fields: {
          filename: { label: EN.filename, placeholder: 'ExcelFileName.xlsx' },
          sheetName: { label: EN.sheet, placeholder: 'Sheet 1' },
        },
      },
    },
    generate: (v) => generateMergingQualifications(v),
  }),
  defineScript({
    slug: 'changing-employee-numbers',
    fields: [
      { name: 'filename', id: 'cen-filename' },
      { name: 'sheetName', id: 'cen-sheet' },
    ],
    text: {
      pl: {
        title: 'Zmiana numerów pracowników',
        description:
          'Zmienia numery zatrudnień hurtowo na podstawie pliku Excel (kolumna 1 to obecny numer pracownika, kolumna 2 — docelowy; wiersz 1 jest pomijany jako nagłówek). Wygenerowany skrypt dopasowuje po `empNumbered:` i zwraca kolekcję numerów, których nie udało się dopasować.',
        fields: {
          filename: { label: PL.filename, placeholder: 'ExcelFileName.xlsx' },
          sheetName: { label: PL.sheet, placeholder: 'Sheet 1' },
        },
      },
      en: {
        title: 'Changing employee numbers',
        description:
          'Renumbers a batch of employments from an Excel file (column 1 as current employee number, column 2 as target; row 1 skipped as header). The generated script matches by `empNumbered:` and returns the collection of numbers that could not be matched.',
        fields: {
          filename: { label: EN.filename, placeholder: 'ExcelFileName.xlsx' },
          sheetName: { label: EN.sheet, placeholder: 'Sheet 1' },
        },
      },
    },
    generate: (v) => generateChangingEmployeeNumbers(v),
  }),
  defineScript({
    slug: 'remove-non-default-grouping-type-languages',
    fields: [],
    text: {
      pl: {
        title: 'Usuwanie nadmiarowych języków typów grupowania',
        navLabel: 'Redukcja języków grupowania',
        description:
          'Sprawdza, w ilu językach wypełnione jest pole `name` każdego `GroupingType`, a następnie zostawia w każdej nazwie tylko tłumaczenie `en_us` — pozostałe języki są usuwane. Wiersze, w których `en_us` jest nil lub puste, zostają bez zmian i trafiają na listę `skipped`. Uruchom w **DBWorkspace** ustawionym na repozytorium klienta; ostatnie wyrażenie zwraca zarówno stan sprzed redukcji, jak i pominięte kody, żeby można było sprawdzić, co się zmieniło.',
        fields: {},
      },
      en: {
        title: 'Remove non-default grouping type languages',
        navLabel: 'Reduce grouping languages',
        description:
          "Audits every `GroupingType` for how many languages populate its `name`, then collapses each name down to just its `en_us` translation — every other locale slot is discarded. Rows whose `en_us` value is nil or empty are left alone and returned in a `skipped` list. Run from a **DBWorkspace** scoped to the customer's repository; the final expression returns both the pre-reduction audit and the skipped codes so you can eyeball what changed.",
        fields: {},
      },
    },
    generate: () => generateRemoveNonDefaultGroupingTypeLanguages(),
  }),
];
